import * as React from 'react';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';
import { EChannelVisibility, EPermissions } from '@common/definitions';
import { useState, useCallback, useMemo, Children } from 'react';
import { Accordion, AccordionItem } from '@szhsin/react-accordion';
import { Button } from '@common/components/button';
import SimpleAccordionItem from '@common/components/accordion/simple-accordion-item';
import Icon from '@common/components/icon';
import { usePermission } from '@common/hooks';
import SearchBar from '@common/components/search-bar';
import leaveGroup from '@modules/social/actions/leave-group';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { ETimelineTypes } from '../../definitions';
import Item from './item';
import PureList from '../../../../common/components/list';

const validVisibilities = [
  EChannelVisibility.PUBLIC,
  EChannelVisibility.PUBLIC_WITH_APPROVAL
];



const ChannelsComponent = ({
  loggedUser,
  organisation,
  network,
  teams,
  channels,
  selected,
  onSelectChannel,
  onHideChannel,
  onMuteChannel,
  networks,
  organisationTeams,
}) => {
  const [searchTerm, setSearchTerm] = useState(undefined);
  const [showMoreNetworks, setShowMoreNetworks] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filterChannels = React.useMemo(() => {
    return (
      channels?.filter((channel) => {
        const isPrivate = channel.visibility === EChannelVisibility.PRIVATE;
        const isPublicOrApproval =
          channel.visibility === EChannelVisibility.PUBLIC ||
          channel.visibility === EChannelVisibility.PUBLIC_WITH_APPROVAL;

        const isMemberOfVisibleChannel =
          channel.is_member && isPublicOrApproval;

        const isVisible = isPrivate || isMemberOfVisibleChannel;
        return isVisible && (searchTerm ? channel.name.toLowerCase().includes(searchTerm.toLowerCase()) : true);
      }) ?? []
    );
  }, [channels, searchTerm]);
  const canViewAllNetworks = usePermission(EPermissions.ORGANISATION_NETWORKS_VIEW_ALL);

  const networksCount = useMemo(() => {
    return networks?.filter((n) => canViewAllNetworks || n.membership).length;
  }, [networks]);
  const filteredNetworks = useMemo(() => {
    return networks
      // @ts-expect-error
      .filter((n) => canViewAllNetworks || n.membership)
      .filter((n) => !searchTerm || n.name.toLowerCase()
        .includes(searchTerm.toLowerCase()))
      .map((n, index) => ({ ...n, originalIndex: index }))
      .sort((a, b) => {
        const aHasMembership = !!a.membership;
        const bHasMembership = !!b.membership;
        if (aHasMembership !== bHasMembership) {
          return aHasMembership ? -1 : 1;
        }
        if (aHasMembership && bHasMembership) {
          return a.originalIndex - b.originalIndex;
        }
        const aStartsWithNumber = /^[0-9]/.test(a.name);
        const bStartsWithNumber = /^[0-9]/.test(b.name);
        if (aStartsWithNumber !== bStartsWithNumber) {
          return aStartsWithNumber ? -1 : 1;
        }
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      })
      .map(({ originalIndex, ...n }) => n);
  }, [canViewAllNetworks, networks, searchTerm]);

  const filteredTeamsBySearch = useMemo(() => {
    if (!searchTerm || !searchTerm.trim() || !networks || !organisationTeams) return [];
    const lowerSearch = searchTerm?.toLowerCase() || '';

    return networks
      .filter((n) => canViewAllNetworks || n.membership)
      .filter((n) => !n.name.toLowerCase().includes(lowerSearch.toLowerCase()))
      .flatMap((n) => organisationTeams
        .filter((team) => team.network_id === n.id)
        .filter((team) => (lowerSearch
          ? n.name.toLowerCase().includes(lowerSearch) ||
                team.name.toLowerCase().includes(lowerSearch)
          : true)
        )
        .map((team) => ({ ...team, networkData: n, networkName: n.name }))
      );
  }, [canViewAllNetworks, networks, organisationTeams, searchTerm]);


  const handleLeaveChannel = useCallback(async (channel) => {
    await dispatch(leaveGroup(organisation.id, { ...channel, is_member: false, access_request_status: null }));
    if (selected?.type === ETimelineTypes.CHANNEL && selected?.id === channel.id) {
      onSelectChannel(ETimelineTypes.FEED, network?.id);
    }
  }, [organisation.id, onSelectChannel, selected]);

  const TeamRender = (args) => {
    return (
      <Link
        to={`/networks/${args?.item?.network_id}/messages/${args?.item?.isGeneral ? 'network-general' : 'team'}/${args?.item?.id}`}
        className="tw-text-inherit hover:tw-text-inherit"
      >
        <Item
          {...args}
        />
      </Link>
    );
  };

  const NetworkRender = ({ children, networkId }) => {
    return (
      <Link
        to={`/networks/${networkId}/messages/network`}
        className="tw-text-inherit tw-w-full hover:tw-text-inherit"
      >
        {children}
      </Link>
    );
  };

  const ChannelRender = (args) => {
    return (
      <Item
        {...args}
        contentWrapper={(children) => (
          <Link
            to={`/networks/${args?.item?.network_id}/messages/channel/${args?.item?.id}`}
            className="tw-text-inherit hover:tw-text-inherit tw-flex tw-flex-1"
          >
            {children}
          </Link>
        )}
      />
    );
  };

  return (
    <div className="balloon Channels">
      <div className="tw-px-3.5">
        <SearchBar
          placeholder={t('social:search_bar_placeholder_community_or_group')}
          onSearch={setSearchTerm}
          debounce={false}
          filter={false}
          value={searchTerm || ''}
          defaultValue={searchTerm}
          clearable
        />
      </div>
      <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-my-2" />
      <Item
        item={{
          name: t('social:channels_my_timeline'),
          image: loggedUser.profile_img,
        }}
        selected={!!(selected && selected.type === ETimelineTypes.FEED)}
        onSelectChannel={() => onSelectChannel(ETimelineTypes.FEED, network.id)}
      />
      <Item
        id="organisation_messages"
        item={{
          name: t('social:company_updates'),
          image: organisation.brand_icon,
        }}
        selected={
            !!(selected && selected.type === ETimelineTypes.ORGANISATION)
          }
        onSelectChannel={() => onSelectChannel(ETimelineTypes.ORGANISATION, organisation.id)}
      />
      <Item
        id="explore_groups"
        selected={
            !!(selected && selected.type === ETimelineTypes.ORGANIZATION_GROUP)
          }
        item={{
          name: t('social:groups_explore'),
          image: '/static/images/social/explore-group.png?url',
        }}
        onSelectChannel={() => onSelectChannel(ETimelineTypes.ORGANIZATION_GROUP, organisation.id)}
      />
      {(filteredNetworks.length > 0 || filteredTeamsBySearch.length > 0) && (
      <h5 className="tw-text-xs tw-font-semibold tw-text-gray-muted tw-p-3.5 tw-mb-0 tw-pl-5">
        <span>{t('social:internal_communities')}</span>
      </h5>
      )}
      {filteredTeamsBySearch.length > 0 && (
      <PureList
        items={filteredTeamsBySearch}
        renderRow={TeamRender}
        rowProps={(team) => ({
          sub: true,
          item: {
            ...team,
            icon: <Icon type="hashtag_icon" className="!tw-w-3.5 !tw-h-3.5 tw-p-[3px]" />,
            isSearch: !!searchTerm,
          },
          selected:
              selected &&
              (selected.type === ETimelineTypes.TEAM || selected.type === ETimelineTypes.NETWORK_GENERAL) &&
              selected.id === team.id,
          onSelectChannel: () => onSelectChannel(team.isGeneral ? ETimelineTypes.NETWORK_GENERAL : ETimelineTypes.TEAM, team.id, true, team.networkData.id),
          onMute:
              team.is_member &&
              ((muted) => onMuteChannel(team.isGeneral ? ETimelineTypes.NETWORK_GENERAL : ETimelineTypes.TEAM, team.id, muted)),
        })}
      />
      )}
      <Accordion transition transitionTimeout={250}>
        {filteredNetworks
          .slice(0, showMoreNetworks ? networks.length : 10)
          .map((networkData, index) => {
            const orgTeams = organisationTeams?.filter(
              (team) => team.network_id === networkData.id
            );
            const isSearchInTeams = searchTerm && orgTeams?.some((team) => team.name.toLowerCase().includes(searchTerm.toLowerCase()));
            if (orgTeams?.length > 0) {
              orgTeams.unshift({
                id: networkData.id,
                name: t('social:timeline_header_network_general'),
                is_member: networkData.is_member,
                isGeneral: true,
                network_id: networkData.id,
              });
            }
            const isSameTeam = selected?.type === ETimelineTypes.TEAM && selected?.parent?.network_id === networkData.id;
            const isSameNetworkGeneral = selected?.type === ETimelineTypes.NETWORK_GENERAL && selected?.id === networkData.id;
            const isOpen = networksCount === 1 || !!(
              selected &&
                (selected.type === ETimelineTypes.NETWORK || selected.type === ETimelineTypes.NETWORK_GENERAL || isSameTeam) &&
                selected.id === networkData.id || isSameNetworkGeneral || isSameTeam || isSearchInTeams
            );
            if (networksCount === 1) {
              return (
                <React.Fragment key={networkData.id}>
                  <div className="tw-flex tw-items-center tw-pr-3.5">
                    <Item
                      addClassName="!tw-flex-1 !tw-pr-3"
                      id="network_feed"
                      isNetwork
                      item={{
                        ...networkData,
                        icon: (
                          <Icon type="community_icon" className="active-icon !tw-w-5 !tw-h-5" />
                        ),
                      }}
                      selected={
                      !!(selected && selected.type === ETimelineTypes.NETWORK && selected.id === networkData.id)
                    }
                      onSelectChannel={() => onSelectChannel(
                        ETimelineTypes.NETWORK,
                        networkData.id,
                        true
                      )}
                      onMute={
                      networkData.is_member &&
                      ((muted) => onMuteChannel(
                        ETimelineTypes.NETWORK,
                        networkData.id,
                        muted
                      ))
                    }
                    />
                    <Icon type="expand_more" className="tw-rotate-180" />
                  </div>
                  <PureList
                    id="network_group_messages"
                    items={orgTeams}
                    renderRow={TeamRender}
                    rowProps={(team) => ({
                      sub: true,
                      item: {
                        ...team,
                        icon: <Icon type="hashtag_icon" className="!tw-w-3.5 !tw-h-3.5 tw-p-[3px]" />,
                        networkName: networkData.name,
                      },
                      selected:
                      selected &&
                      (selected.type === ETimelineTypes.TEAM || selected.type === ETimelineTypes.NETWORK_GENERAL) &&
                      selected.id === team.id,
                      onSelectChannel: () => onSelectChannel(team.isGeneral ? ETimelineTypes.NETWORK_GENERAL : ETimelineTypes.TEAM, team.id, true, networkData.id),
                      onMute:
                      team.is_member &&
                      ((muted) => onMuteChannel(team.isGeneral ? ETimelineTypes.NETWORK_GENERAL : ETimelineTypes.TEAM, team.id, muted)),
                    })}
                  />
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={networkData.id}>
                <SimpleAccordionItem
                  className={`community-accordion-item ${selected && selected.type === ETimelineTypes.NETWORK && selected.id === networkData.id ? 'active' : ''}`}
                  key={networkData.id}
                  customHeader={
                    <NetworkRender networkId={networkData.id}>
                      <Item
                        addClassName="!tw-flex-1"
                        id="network_feed"
                        isNetwork
                        item={{
                          ...networkData,
                          icon: (
                            <Icon type="community_icon" className="active-icon !tw-w-5 !tw-h-5" />
                          ),
                        }}
                        selected={
                        !!(selected && selected.type === ETimelineTypes.NETWORK && selected.id === networkData.id)
                      }
                        onSelectChannel={() => onSelectChannel(
                          ETimelineTypes.NETWORK,
                          networkData.id,
                          true
                        )}
                        onMute={
                        networkData.is_member &&
                        ((muted) => onMuteChannel(
                          ETimelineTypes.NETWORK,
                          networkData.id,
                          muted
                        ))
                      }
                      />
                    </NetworkRender>
                  }
                  initialEntered={isOpen}
                  iconShow={orgTeams.length > 0}
                >
                  <PureList
                    id="network_group_messages"
                    items={orgTeams}
                    renderRow={TeamRender}
                    rowProps={(team) => ({
                      sub: true,
                      item: {
                        ...team,
                        icon: <Icon type="hashtag_icon" className="!tw-w-3.5 !tw-h-3.5 tw-p-[3px]" />,
                        networkName: networkData.name,
                      },
                      selected:
                        selected &&
                        (selected.type === ETimelineTypes.TEAM || selected.type === ETimelineTypes.NETWORK_GENERAL) &&
                        selected.id === team.id,
                      onSelectChannel: () => onSelectChannel(team.isGeneral ? ETimelineTypes.NETWORK_GENERAL : ETimelineTypes.TEAM, team.id, true, networkData.id),
                      onMute:
                        team.is_member &&
                        ((muted) => onMuteChannel(team.isGeneral ? ETimelineTypes.NETWORK_GENERAL : ETimelineTypes.TEAM, team.id, muted)),
                    })}
                  />
                </SimpleAccordionItem>
                <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-my-2" />
              </React.Fragment>
            );
          })}
        {filteredNetworks.length > 10 && (
        <>
          <div className="tw-flex tw-justify-center">
            <Button
              onClick={() => setShowMoreNetworks(!showMoreNetworks)}
              type="transparent"
              className="!tw-py-0.5"
            >
              {showMoreNetworks ? t('social:show_less') : t('social:show_more')}
            </Button>
          </div>
          <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-my-2" />

        </>
        )}
      </Accordion>
      {filterChannels.length > 0 && (
      <h5 className="tw-text-xs tw-font-semibold tw-text-gray-muted tw-p-3.5 tw-mb-0 tw-pl-5">
        <span>{t('social:organisation_groups')}</span>
      </h5>
      )}
      <PureList
        id="organisation_group_messages"
        items={filterChannels.map((channel) => R.assoc(
          'image',
          channel.theme.logo_url || organisation.brand_icon,
          channel
        )
        )}
        renderRow={ChannelRender}
        rowProps={(group) => ({
          selected:
              selected &&
              selected.type === ETimelineTypes.CHANNEL &&
              selected.id === group.id,
          item: {
            ...group,
            network_id: network?.id,
          },
          onSelectChannel: () => onSelectChannel(ETimelineTypes.CHANNEL, group.id),
          onHideChannel: (hidden) => onHideChannel(ETimelineTypes.CHANNEL, group.id, hidden),
          onLeaveChannel: validVisibilities.includes(group.visibility) && (() => handleLeaveChannel(group)),
          onMute:
              group.settings.is_member &&
              ((muted) => onMuteChannel(ETimelineTypes.CHANNEL, group.id, muted)),
        })}
      />
    </div>
  );
};

export default ChannelsComponent;
